import { Link } from 'react-router-dom';
import MenuBar from './Component/MenuBar';
import Sidebar from './Component/Sidebar';
import Footer from './Component/Footer';
import './AboutPage.css';
import './LegalPage.css';

const LAST_UPDATED = 'September 25, 2026';

// Section list used for the table of contents and anchor ids
const SECTIONS = [
    { id: 'who-we-are', title: 'Who We Are' },
    { id: 'information-we-collect', title: 'Information We Collect' },
    { id: 'how-we-use-information', title: 'How We Use Your Information' },
    { id: 'google-user-data', title: 'Google User Data (Gmail)' },
    { id: 'sharing', title: 'How We Share Information' },
    { id: 'payments', title: 'Payments' },
    { id: 'cookies', title: 'Cookies and Analytics' },
    { id: 'retention-security', title: 'Data Retention and Security' },
    { id: 'your-choices', title: 'Your Choices and Rights' },
    { id: 'children', title: "Children's Privacy" },
    { id: 'changes', title: 'Changes to This Policy' },
    { id: 'contact', title: 'Contact Us' },
];

const PrivacyPolicy = () => {
    return (
        <>
            <MenuBar />
            <Sidebar />
            <main className="yk-about-page yk-legal-page">
                {/* <!-- Page Banner Section --> */}
                <section className="yk-about-hero" aria-labelledby="yk-privacy-title">
                    <div className="yk-about-hero-image" aria-hidden="true"></div>
                    <div className="auto-container">
                        <nav className="yk-about-breadcrumb" aria-label="Breadcrumb">
                            <ol>
                                <li><Link to="/USA/Home">Home</Link></li>
                                <li aria-current="page">Privacy Policy</li>
                            </ol>
                        </nav>
                        <h1 id="yk-privacy-title">Privacy Policy</h1>
                    </div>
                </section>
                {/* <!--End Banner Section --> */}

                <section className="yk-legal-content">
                    <div className="auto-container">
                        <p className="yk-legal-updated">Last updated: {LAST_UPDATED}</p>

                        <p className="yk-legal-intro">
                            Radha Madhav Society, operating Yugal Kunj – Radha Krishna Temple and Community Center
                            ("Yugal Kunj", "we", "us"), respects your privacy. This Privacy Policy explains what
                            information we collect through yugalkunj.org and our related portals and applications
                            (the "Services"), how we use it, and the choices you have.
                        </p>

                        <nav className="yk-legal-toc" aria-labelledby="yk-privacy-toc-heading">
                            <h2 id="yk-privacy-toc-heading">Contents</h2>
                            <ol>
                                {SECTIONS.map((section) => (
                                    <li key={section.id}><a href={`#${section.id}`}>{section.title}</a></li>
                                ))}
                            </ol>
                        </nav>

                        <section id="who-we-are" className="yk-legal-section" aria-labelledby="who-we-are-heading">
                            <h2 id="who-we-are-heading">1. Who We Are</h2>
                            <p>
                                Radha Madhav Society is a non-profit charitable organization functioning under the
                                auspices of Jagadguru Kripalu Parishat. Yugal Kunj is located at 2769 Duluth Hwy,
                                Duluth, GA 30096, USA. We offer temple services, classes, camps, devotional retreats,
                                volunteer programs, event rentals and weekly "Words of Wisdom" spiritual messages.
                            </p>
                        </section>

                        <section id="information-we-collect" className="yk-legal-section" aria-labelledby="information-we-collect-heading">
                            <h2 id="information-we-collect-heading">2. Information We Collect</h2>
                            <h3>Information you give us</h3>
                            <ul>
                                <li><strong>Contact details</strong> – name, email address, phone number and postal address when you contact us, subscribe, register or become a member.</li>
                                <li><strong>Registration details</strong> – participant names, ages and emergency contacts for classes, camps, retreats and events; volunteer availability and waiver acknowledgements.</li>
                                <li><strong>Payment details</strong> – billing name and address. Card and bank details are entered directly with our payment processors and are not stored on our servers.</li>
                                <li><strong>Messages</strong> – anything you send us through contact or inquiry forms.</li>
                            </ul>
                            <h3>Information collected automatically</h3>
                            <ul>
                                <li>Device and browser type, IP address, pages visited and referring pages, collected through standard server logs and cookies (see Section 7).</li>
                            </ul>
                        </section>

                        <section id="how-we-use-information" className="yk-legal-section" aria-labelledby="how-we-use-information-heading">
                            <h2 id="how-we-use-information-heading">3. How We Use Your Information</h2>
                            <ul>
                                <li>To process registrations, memberships, donations and event rentals.</li>
                                <li>To respond to your inquiries and send confirmations, receipts and schedule updates.</li>
                                <li>To send the weekly "Words of Wisdom" email and other spiritual or program announcements you have subscribed to.</li>
                                <li>To operate, secure and improve the Services.</li>
                                <li>To comply with legal obligations and protect the safety of participants, volunteers and visitors.</li>
                            </ul>
                        </section>

                        <section id="google-user-data" className="yk-legal-section" aria-labelledby="google-user-data-heading">
                            <h2 id="google-user-data-heading">4. Google User Data (Gmail)</h2>
                            <p>
                                Our "Words of Wisdom" feature publishes the weekly spiritual message written by Sushri
                                Siddheshvari Devi Ji (Didi Ji) that is distributed by email from Radha Madhav Society.
                                To do this, our server-side application connects to <strong>one organization-controlled
                                Gmail mailbox</strong> using Google's Gmail API with the read-only scope
                                (<code>https://www.googleapis.com/auth/gmail.readonly</code>).
                            </p>
                            <ul>
                                <li><strong>What we access:</strong> only messages in that mailbox matching the "Words of Wisdom" sender and subject filter — the subject, sender, date and message body (including inline images).</li>
                                <li><strong>How we use it:</strong> to store each Words of Wisdom message in our database and display it on this website and forward it to subscribers who have opted in. We do not read, store or process any other email.</li>
                                <li><strong>What we do not do:</strong> we do not access any visitor's or member's Gmail account, we do not sell Google user data, we do not use it for advertising, and no human reads the mailbox content through our application except as needed for security, troubleshooting or legal compliance.</li>
                                <li><strong>Limited Use:</strong> our use of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</li>
                                <li><strong>Revoking access:</strong> the mailbox owner may revoke our application's access at any time from <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</a>.</li>
                            </ul>
                        </section>

                        <section id="sharing" className="yk-legal-section" aria-labelledby="sharing-heading">
                            <h2 id="sharing-heading">5. How We Share Information</h2>
                            <p>We do not sell or rent personal information. We share it only:</p>
                            <ul>
                                <li>With <strong>service providers</strong> that host our systems, send email on our behalf, or process payments, and only for those purposes.</li>
                                <li>With <strong>affiliated Radha Madhav Society centers</strong> when you register for a program run at another location.</li>
                                <li>When <strong>required by law</strong>, to protect our rights, or to protect the safety of any person.</li>
                            </ul>
                        </section>

                        <section id="payments" className="yk-legal-section" aria-labelledby="payments-heading">
                            <h2 id="payments-heading">6. Payments</h2>
                            <p>
                                Payments for registrations, memberships and donations are processed by PCI-compliant
                                third-party payment processors (for example Authorize.Net). Your card or bank details
                                are transmitted directly to the processor; we receive only a confirmation, the last
                                four digits and the transaction reference needed for receipts and reconciliation.
                            </p>
                        </section>

                        <section id="cookies" className="yk-legal-section" aria-labelledby="cookies-heading">
                            <h2 id="cookies-heading">7. Cookies and Analytics</h2>
                            <p>
                                We use essential cookies and browser storage to keep you signed in to our portals and to
                                remember preferences. We may use privacy-respecting analytics to understand which pages
                                are visited. You can disable cookies in your browser settings; some portal features may
                                then not work.
                            </p>
                        </section>

                        <section id="retention-security" className="yk-legal-section" aria-labelledby="retention-security-heading">
                            <h2 id="retention-security-heading">8. Data Retention and Security</h2>
                            <p>
                                We keep personal information for as long as needed to provide the Services, meet
                                legal, accounting and reporting requirements, and maintain program history. We protect
                                information with encrypted connections (HTTPS), access controls, and secure storage of
                                credentials. No method of transmission or storage is completely secure, so we cannot
                                guarantee absolute security.
                            </p>
                        </section>

                        <section id="your-choices" className="yk-legal-section" aria-labelledby="your-choices-heading">
                            <h2 id="your-choices-heading">9. Your Choices and Rights</h2>
                            <ul>
                                <li><strong>Email:</strong> you may unsubscribe from Words of Wisdom or announcement emails at any time using the link in the email or by contacting us.</li>
                                <li><strong>Access and correction:</strong> you may request a copy of, or corrections to, the personal information we hold about you.</li>
                                <li><strong>Deletion:</strong> you may ask us to delete your information, subject to records we must retain by law.</li>
                            </ul>
                            <p>To exercise these rights, contact us using the details in Section 12.</p>
                        </section>

                        <section id="children" className="yk-legal-section" aria-labelledby="children-heading">
                            <h2 id="children-heading">10. Children's Privacy</h2>
                            <p>
                                Many of our programs are for children. We collect a child's information only from a
                                parent or guardian during registration and use it solely to run the program safely.
                                We do not knowingly collect information directly from children under 13 online.
                            </p>
                        </section>

                        <section id="changes" className="yk-legal-section" aria-labelledby="changes-heading">
                            <h2 id="changes-heading">11. Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. The "Last updated" date at the top
                                shows when it was last revised. Continued use of the Services after a change means you
                                accept the updated policy.
                            </p>
                        </section>

                        <section id="contact" className="yk-legal-section" aria-labelledby="contact-heading">
                            <h2 id="contact-heading">12. Contact Us</h2>
                            <div className="yk-legal-contact">
                                <address>
                                    Radha Madhav Society – Yugal Kunj<br />
                                    2769 Duluth Hwy, Duluth, GA 30096, USA
                                </address>
                                <ul>
                                    <li>Email: <a href="mailto:info@yugalkunj.org">info@yugalkunj.org</a></li>
                                    <li>Phone: <a href="tel:678-920-6669">678-920-6669</a></li>
                                </ul>
                            </div>
                            <p style={{ marginTop: '20px' }}>
                                See also our <Link to="/USA/terms-of-service">Terms of Service</Link>.
                            </p>
                        </section>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default PrivacyPolicy;
